import React, { useState, useEffect } from 'react';
import { Power, Wifi, WifiOff, RefreshCw, AlertTriangle, X, CheckCircle, Trash2, Eye } from 'lucide-react';
import { tradeSettingsAPI, liveDataAPI } from '../services/api';
import { API_CONFIG } from '../services/config';
import { useTheme } from '../contexts/ThemeContext';
import { useNifty50Ltp } from '../hooks/useNifty50Ltp';
import { spreadSettingsAPI, SpreadSettingRow } from '../services/spreadApi';
import { SpreadMarket, SpreadDirection } from '../types/spread';

interface TradingAppBarProps {
  onTradeToggle?: (enabled: boolean) => void;
  onCallToggle?: (enabled: boolean) => void;
  onPutToggle?: (enabled: boolean) => void;
  pnlRs?: number;
  pnlPercent?: number;
  onExit50?: () => void;
  onExitAll?: () => void;
}

const TradingAppBar: React.FC<TradingAppBarProps> = ({
  onTradeToggle,
  onCallToggle,
  onPutToggle,
  pnlRs = 0,
  pnlPercent = 0,
  onExit50,
  onExitAll
}) => {
  const { themeConfig } = useTheme();
  const [tradeEnabled, setTradeEnabled] = useState(false);
  const [callEnabled, setCallEnabled] = useState(false);
  const [putEnabled, setPutEnabled] = useState(false);
  // const [exitMode, setExitMode] = useState<'OFF' | '50%' | 'ALL'>('OFF'); // Commented for future use
  const [showExitModal, setShowExitModal] = useState(false);
  const [exitModalType, setExitModalType] = useState<'NSE' | 'BSE'>('NSE');
  const [exitOrders, setExitOrders] = useState<any[]>([]);
  const [exitProgress, setExitProgress] = useState<{ [key: number]: 'pending' | 'processing' | 'success' | 'failed' }>({});
  const [isExiting, setIsExiting] = useState(false);
  const [trueDataConnected, setTrueDataConnected] = useState(false);
  const [trueDataMode, setTrueDataMode] = useState<'live' | 'replay' | 'unknown'>('unknown');
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [lowLimitCheckEnabled, setLowLimitCheckEnabled] = useState(false);
  const [lowLimitX, setLowLimitX] = useState(15);
  const [lowLimitY, setLowLimitY] = useState(12);
  const [realPnlRs, setRealPnlRs] = useState(0);
  const [realPnlPercent, setRealPnlPercent] = useState(0);
  const [xPercentage, setXPercentage] = useState(5);
  const [yPercentage, setYPercentage] = useState(3);
  // Which stock pool feeds the X/Y breadth calculation — stored on the NSE trade_settings row
  // regardless of which N/B tab is active, since the calculation itself isn't exchange-specific.
  const [xyStockMode, setXyStockMode] = useState<number>(1);
  const [showXYSymbolsModal, setShowXYSymbolsModal] = useState(false);
  const [xySymbolsLoading, setXySymbolsLoading] = useState(false);
  const [xySymbolsError, setXySymbolsError] = useState('');
  const [xySymbolsData, setXySymbolsData] = useState<{ mode: number; count: number; symbols: string[] }>({ mode: 1, count: 0, symbols: [] });
  const [xCount, setXCount] = useState('0');
  const [yCount, setYCount] = useState('0');
  const [xPerAvg, setXPerAvg] = useState('0.00');
  const [yPerAvg, setYPerAvg] = useState('0.00');
  const [niftyLtp, setNiftyLtp] = useState(0);
  const [sensexLtp, setSensexLtp] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState<'NSE' | 'BSE'>('NSE');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [deletedCount, setDeletedCount] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);
  const [ordersCount, setOrdersCount] = useState(0);
  const [bseOrdersCount, setBseOrdersCount] = useState(0);
  const [openOrders, setOpenOrders] = useState<any[]>([]);
  const [openOrdersCount, setOpenOrdersCount] = useState(0);
  const [buyLtpDiff, setBuyLtpDiff] = useState({ active: false, entryLtp: 0, diff: 0 });
  const [sellLtpDiff, setSellLtpDiff] = useState({ active: false, entryLtp: 0, diff: 0 });
  const [tagValue, setTagValue] = useState<string>('');
  const [tslValue, setTslValue] = useState<string>('');
  const [nseOrderEnabled, setNseOrderEnabled] = useState(true);
  const [bseOrderEnabled, setBseOrderEnabled] = useState(true);
  const [spreadSettings, setSpreadSettings] = useState<SpreadSettingRow[]>([]);
  const [exchange, setExchange] = useState<'NSE' | 'BSE'>('NSE');

  // Use instant NIFTY 50 LTP hook
  useNifty50Ltp({
    autoStart: true,
    updateInterval: 50,
    useStreaming: true
  });

  useEffect(() => {
    fetchTradeSettings();
    fetchXYStockMode();
    checkTrueDataConnection();
    fetchTimerStatus();
    fetchPositions();
    fetchOpenOrdersForDiff(exchange);
    fetchLiveCounts();
    fetchOrdersCount();
    fetchSpreadSettings();

    // Check TrueData connection every 5 seconds
    const interval = setInterval(checkTrueDataConnection, 5000);

    // Update timer every second
    const timerInterval = setInterval(() => {
      if (timerRunning) {
        setTimerSeconds(prev => prev + 1);
      }
    }, 1000);

    // Fetch timer status every 2 seconds
    const timerStatusInterval = setInterval(fetchTimerStatus, 2000);

    // Fetch positions every 5 seconds — pass exchange explicitly to avoid stale closure
    const positionsInterval = setInterval(() => {
      fetchPositions();
      fetchOpenOrdersForDiff(exchange);
      fetchOrdersCount();
      fetchSpreadSettings();
    }, 5000);

    // Fetch live counts every 250ms
    const liveCountsInterval = setInterval(fetchLiveCounts, 250);

    return () => {
      clearInterval(interval);
      clearInterval(timerInterval);
      clearInterval(timerStatusInterval);
      clearInterval(positionsInterval);
      clearInterval(liveCountsInterval);
    };
  }, [timerRunning, exchange]);

  const fetchSpreadSettings = async () => {
    try {
      const response = await spreadSettingsAPI.getSettings();
      setSpreadSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch spread settings:', error);
    }
  };

  const isSpreadEnabled = (market: SpreadMarket, direction: SpreadDirection): boolean =>
    !!spreadSettings.find(s => s.market === market && s.direction === direction)?.is_enabled;

  const toggleSpreadEnabled = async (market: SpreadMarket, direction: SpreadDirection) => {
    const next = !isSpreadEnabled(market, direction);
    // Optimistic update so the button flips instantly
    setSpreadSettings(prev => {
      const existing = prev.find(s => s.market === market && s.direction === direction);
      if (existing) {
        return prev.map(s => (s.market === market && s.direction === direction) ? { ...s, is_enabled: next } : s);
      }
      return [...prev, { market, direction, is_enabled: next, default_delay_seconds: 3 }];
    });
    try {
      await spreadSettingsAPI.updateSetting(market, direction, next);
    } catch (error) {
      console.error('Failed to update spread master switch:', error);
      fetchSpreadSettings(); // revert to server truth on failure
    }
  };

  const fetchTradeSettings = async (exch?: 'NSE' | 'BSE') => {
    try {
      const settings = await tradeSettingsAPI.getSettings(exch || exchange);
      setTradeEnabled(settings.trade_status === 'ON');
      setCallEnabled(settings.call_status === 'ON');
      setPutEnabled(settings.put_status === 'ON');
      setXPercentage(settings.x_percentage || 5);
      setYPercentage(settings.y_percentage || 3);

      // Commented for future use
      // if (settings.exit_50_status === 'ON') {
      //   setExitMode('50%');
      // } else if (settings.exit_all_status === 'ON') {
      //   setExitMode('ALL');
      // } else {
      //   setExitMode('OFF');
      // }

      setLowLimitCheckEnabled(settings.low_limit_check_xy === 'ON');
      setLowLimitX(settings.low_limit_x || 15);
      setLowLimitY(settings.low_limit_y || 12);
      setTagValue(settings.tag_value ? settings.tag_value.toString() : '');
      setTslValue(settings.tsl_value || '');
      setNseOrderEnabled(settings.nse_order_enabled !== 'OFF');
      setBseOrderEnabled(settings.bse_order_enabled !== 'OFF');
    } catch (error) {
      console.error('Failed to fetch trade settings:', error);
    }
  };

  // Always reads/writes the NSE row — xy_stock_mode isn't exchange-specific, so it must not
  // follow whichever N/B tab happens to be selected in the app bar.
  const fetchXYStockMode = async () => {
    try {
      const settings = await tradeSettingsAPI.getSettings('NSE');
      setXyStockMode(settings.xy_stock_mode || 1);
    } catch (error) {
      console.error('Failed to fetch xy_stock_mode:', error);
    }
  };

  const updateXYStockMode = async (mode: number) => {
    const prev = xyStockMode;
    setXyStockMode(mode); // optimistic
    try {
      await tradeSettingsAPI.updateSettings({ xy_stock_mode: mode, exchange: 'NSE' });
    } catch (error) {
      console.error('Failed to update xy_stock_mode:', error);
      setXyStockMode(prev); // revert on failure
    }
  };

  // Shows exactly which symbols are live-feeding the X/Y calculation right now — reads the
  // monitor's actual in-memory list, not just the selected mode, so a mode switch can be
  // visually confirmed instead of assumed.
  const openXYSymbolsModal = async () => {
    setShowXYSymbolsModal(true);
    setXySymbolsLoading(true);
    setXySymbolsError('');
    try {
      const data = await liveDataAPI.getXYStockSymbols();
      setXySymbolsData(data);
    } catch (error) {
      console.error('Failed to fetch XY stock symbols:', error);
      setXySymbolsError('Failed to load symbols');
    } finally {
      setXySymbolsLoading(false);
    }
  };

  const checkTrueDataConnection = async () => {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/health/truedata`);
      const data = await response.json();
      setTrueDataConnected(data.connected);
      setTrueDataMode(data.mode || 'unknown');
    } catch (error) {
      setTrueDataConnected(false);
      setTrueDataMode('unknown');
    }
  };

  const fetchPositions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.baseURL}/dhan/positions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.summary) {
        setRealPnlRs(data.summary.totalPnlRs || 0);
        setRealPnlPercent(data.summary.pnlPercent || 0);
      }
    } catch (error) {
      console.error('Failed to fetch positions:', error);
    }
  };

  const fetchLiveCounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const [countsRes, niftyRes, sensexRes] = await Promise.all([
        fetch(`${API_CONFIG.baseURL}/live-data/counts`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_CONFIG.baseURL}/nifty50-ltp/instant`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_CONFIG.baseURL}/nifty50-ltp/sensex-instant`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      const countsData = await countsRes.json();
      const niftyData = await niftyRes.json();
      const sensexData = await sensexRes.json();
      setXCount(countsData.x_count?.toString() || '0');
      setYCount(countsData.y_count?.toString() || '0');
      if (countsData.x_per_avg !== undefined && countsData.x_per_avg !== null) setXPerAvg(countsData.x_per_avg.toFixed(2));
      if (countsData.y_per_avg !== undefined && countsData.y_per_avg !== null) setYPerAvg(countsData.y_per_avg.toFixed(2));
      if (niftyData.ltp > 0) setNiftyLtp(niftyData.ltp);
      if (sensexData.ltp > 0) setSensexLtp(sensexData.ltp);
    } catch (error) {
      console.error('Failed to fetch live counts:', error);
    }
  };

  const fetchOpenOrdersForDiff = async (exch?: 'NSE' | 'BSE') => {
    try {
      const token = localStorage.getItem('token');
      const activeExchange = exch || exchange;
      const endpoint = activeExchange === 'BSE' ? '/bse/orders/open' : '/orders/open';
      const response = await fetch(`${API_CONFIG.baseURL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      const orders = Array.isArray(data) ? data : [];
      setOpenOrders(orders);
      setOpenOrdersCount(orders.length);
    } catch (error) {
      console.error('Failed to fetch open orders for diff:', error);
    }
  };

  const fetchOrdersCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const [nseRes, bseRes] = await Promise.all([
        fetch(`${API_CONFIG.baseURL}/orders?page=1&limit=1`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_CONFIG.baseURL}/bse/orders?page=1&limit=1`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      const nseData = await nseRes.json();
      const bseData = await bseRes.json();
      setOrdersCount(nseData.pagination?.total || 0);
      setBseOrdersCount(bseData.pagination?.total || 0);
    } catch {}
  };

  // Real-time spot difference calculation — NSE uses live niftyLtp, BSE uses live sensexLtp
  useEffect(() => {
    if (exchange === 'NSE') {
      if (niftyLtp <= 0) return;
      const buyOrders = openOrders.filter((o: any) => o.drv_option_type === 'CALL' && parseFloat(o.nifty_entry_ltp) > 0);
      const sellOrders = openOrders.filter((o: any) => o.drv_option_type === 'PUT' && parseFloat(o.nifty_entry_ltp) > 0);
      if (buyOrders.length > 0) {
        const entryLtp = parseFloat(buyOrders[0].nifty_entry_ltp);
        setBuyLtpDiff({ active: true, entryLtp, diff: parseFloat((niftyLtp - entryLtp).toFixed(2)) });
      } else {
        setBuyLtpDiff({ active: false, entryLtp: 0, diff: 0 });
      }
      if (sellOrders.length > 0) {
        const entryLtp = parseFloat(sellOrders[0].nifty_entry_ltp);
        setSellLtpDiff({ active: true, entryLtp, diff: parseFloat((niftyLtp - entryLtp).toFixed(2)) });
      } else {
        setSellLtpDiff({ active: false, entryLtp: 0, diff: 0 });
      }
    } else {
      // BSE: use live sensexLtp against sensex_entry_ltp stored at order placement time
      if (sensexLtp <= 0) return;
      const buyOrders = openOrders.filter((o: any) => o.drv_option_type === 'CALL' && parseFloat(o.sensex_entry_ltp) > 0);
      const sellOrders = openOrders.filter((o: any) => o.drv_option_type === 'PUT' && parseFloat(o.sensex_entry_ltp) > 0);
      if (buyOrders.length > 0) {
        const entryLtp = parseFloat(buyOrders[0].sensex_entry_ltp);
        setBuyLtpDiff({ active: true, entryLtp, diff: parseFloat((sensexLtp - entryLtp).toFixed(2)) });
      } else {
        setBuyLtpDiff({ active: false, entryLtp: 0, diff: 0 });
      }
      if (sellOrders.length > 0) {
        const entryLtp = parseFloat(sellOrders[0].sensex_entry_ltp);
        setSellLtpDiff({ active: true, entryLtp, diff: parseFloat((sensexLtp - entryLtp).toFixed(2)) });
      } else {
        setSellLtpDiff({ active: false, entryLtp: 0, diff: 0 });
      }
    }
  }, [niftyLtp, sensexLtp, openOrders, exchange]);

  const fetchTimerStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.baseURL}/timer/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.is_running && data.start_time) {
        const startTime = new Date(data.start_time).getTime();
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setTimerSeconds(elapsed);
        setTimerRunning(true);
      } else {
        setTimerSeconds(data.duration_seconds || 0);
        setTimerRunning(false);
      }
    } catch (error) {
      console.error('Failed to fetch timer status:', error);
    }
  };

  const handleResetTimer = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_CONFIG.baseURL}/timer/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTimerSeconds(0);
      setTimerRunning(false);
    } catch (error) {
      console.error('Failed to reset timer:', error);
    }
  };

  const formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const updateSettings = async (updates: any) => {
    try {
      await tradeSettingsAPI.updateSettings({
        trade_status: tradeEnabled ? 'ON' : 'OFF',
        call_status: callEnabled ? 'ON' : 'OFF',
        put_status: putEnabled ? 'ON' : 'OFF',
        exit_50_status: 'OFF', // Commented for future use
        exit_all_status: 'OFF', // Commented for future use
        low_limit_check_xy: lowLimitCheckEnabled ? 'ON' : 'OFF',
        low_limit_x: lowLimitX,
        low_limit_y: lowLimitY,
        tag_value: tagValue !== '' ? parseInt(tagValue) : 0,
        tsl_value: tslValue,
        ...updates,
        exchange: exchange
      });
    } catch (error) {
      console.error('Failed to update trade settings:', error);
    }
  };

  const handleTradeToggle = () => {
    const newState = !tradeEnabled;
    setTradeEnabled(newState);

    if (!newState) {
      // If turning trade OFF, also turn off CALL, PUT, and LL
      setCallEnabled(false);
      setPutEnabled(false);
      setLowLimitCheckEnabled(false);
      onCallToggle?.(false);
      onPutToggle?.(false);
      updateSettings({
        trade_status: 'OFF',
        call_status: 'OFF',
        put_status: 'OFF',
        low_limit_check_xy: 'OFF'
      });
    } else {
      // If turning trade ON, turn on CALL and PUT but keep LL OFF
      setCallEnabled(true);
      setPutEnabled(true);
      setLowLimitCheckEnabled(false);
      onCallToggle?.(true);
      onPutToggle?.(true);
      updateSettings({
        trade_status: 'ON',
        call_status: 'ON',
        put_status: 'ON',
        low_limit_check_xy: 'OFF'
      });
    }

    onTradeToggle?.(newState);
  };

  const handleCallToggle = () => {
    const newState = !callEnabled;
    setCallEnabled(newState);
    onCallToggle?.(newState);
    updateSettings({ call_status: newState ? 'ON' : 'OFF' });
  };

  const handlePutToggle = () => {
    const newState = !putEnabled;
    setPutEnabled(newState);
    onPutToggle?.(newState);
    updateSettings({ put_status: newState ? 'ON' : 'OFF' });
  };

  // Commented for future use
  // const handleExit50Toggle = () => {
  //   onExit50?.();
  //   updateSettings({ exit_50_status: 'ON', exit_all_status: 'OFF' });
  // };

  const handleExitAllClick = async (type: 'NSE' | 'BSE') => {
    const endpoint = type === 'NSE' ? '/orders/open' : '/bse/orders/open';
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_CONFIG.baseURL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const orders = await response.json();
      setExitModalType(type);
      setExitOrders(Array.isArray(orders) ? orders : []);
      const initialProgress: any = {};
      (Array.isArray(orders) ? orders : []).forEach((order: any) => {
        initialProgress[order.id] = 'pending';
      });
      setExitProgress(initialProgress);
      setShowExitModal(true);
    } catch (error) {
      console.error('Failed to fetch open orders:', error);
      setExitModalType(type);
      setExitOrders([]);
      setShowExitModal(true);
    }
  };

  const handleConfirmExitAll = async () => {
    setIsExiting(true);
    const endpoint = exitModalType === 'NSE' ? '/orders/exit-all' : '/bse/orders/exit-all';
    try {
      const token = localStorage.getItem('token');
      const processingProgress: any = {};
      exitOrders.forEach((order: any) => { processingProgress[order.id] = 'processing'; });
      setExitProgress(processingProgress);

      const response = await fetch(`${API_CONFIG.baseURL}${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      const resultProgress: any = {};
      exitOrders.forEach((order: any) => {
        resultProgress[order.id] = response.ok ? 'success' : 'failed';
      });
      setExitProgress(resultProgress);

      onExitAll?.();
      setTimeout(() => { setShowExitModal(false); setIsExiting(false); }, 2000);
    } catch (error) {
      console.error('Exit all failed:', error);
      const failedProgress: any = {};
      exitOrders.forEach((order: any) => { failedProgress[order.id] = 'failed'; });
      setExitProgress(failedProgress);
      setIsExiting(false);
    }
  };

  const handlePermanentDelete = async () => {
    setShowDeleteModal(false);
    const count = deleteType === 'NSE' ? ordersCount : bseOrdersCount;
    if (count === 0) return;
    setDeleting(true);
    setDeleteProgress(0);
    const endpoint = deleteType === 'NSE' ? '/orders/delete-permanently' : '/bse/orders/delete-permanently';
    try {
      const progressInterval = setInterval(() => {
        setDeleteProgress(prev => Math.min(prev + 10, 90));
      }, 100);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.baseURL}${endpoint}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      clearInterval(progressInterval);
      setDeleteProgress(100);
      if (response.ok) {
        const result = await response.json();
        setTimeout(() => {
          setDeleting(false);
          setDeleteProgress(0);
          setDeletedCount(result.deletedCount || 0);
          setShowSuccessModal(true);
        }, 500);
      } else {
        const errorData = await response.json();
        setDeleting(false);
        setDeleteProgress(0);
        alert(`❌ Failed to delete orders: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Permanent delete failed:', error);
      setDeleting(false);
      setDeleteProgress(0);
      alert('❌ Error occurred during permanent delete');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="trading-appbar-root" style={{ margin: '16px' }}>
      {/* Tablet view: client's explicit ask — shrink font/padding/gaps so the WHOLE bar (both
          rows) fits without needing the horizontal scroll below, without removing any control.
          `zoom` scales this entire subtree (text, padding, gaps, borders) together proportionally
          in one step, instead of hand-tuning each of this file's 70+ fontSize/55+ padding
          declarations individually — widely supported in Chromium/Safari/current Firefox.
          overflowX:'auto' on each row stays as a safety net for anything narrower than this
          covers, not removed. 992px matches MainLayout.tsx's own tablet breakpoint. */}
      <style>{`@media (max-width: 992px) { .trading-appbar-root { zoom: 0.5; } }`}</style>
      {/* First Row */}
      <div style={{
        background: themeConfig.colors.header,
        border: `1px solid ${themeConfig.colors.border}`,
        borderRadius: '4px 4px 0 0',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'nowrap',
        gap: '12px',
        overflowX: 'auto',
        boxShadow: `0 2px 4px ${themeConfig.colors.shadow}`
      }}>
       {/* Exchange Toggle */}
      <div style={{ display: 'flex', gap: '2px' }}>
        <button
          onClick={() => { setExchange('NSE'); fetchTradeSettings('NSE'); fetchOpenOrdersForDiff('NSE'); }}
          style={{
            background: exchange === 'NSE' ? '#0d6efd' : '#e9ecef',
            color: exchange === 'NSE' ? 'white' : '#495057',
            border: 'none',
            borderRadius: '4px 0 0 4px',
            padding: '4px 10px',
            fontSize: '11px',
            fontWeight: '700',
            cursor: 'pointer'
          }}
        >
          NSE
        </button>
        <button
          onClick={() => { setExchange('BSE'); fetchTradeSettings('BSE'); fetchOpenOrdersForDiff('BSE'); }}
          style={{
            background: exchange === 'BSE' ? '#8b1538' : '#e9ecef',
            color: exchange === 'BSE' ? 'white' : '#495057',
            border: 'none',
            borderRadius: '0 4px 4px 0',
            padding: '4px 10px',
            fontSize: '11px',
            fontWeight: '700',
            cursor: 'pointer'
          }}
        >
          BSE
        </button>
      </div>

       {/* TrueData Connection Status */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '6px 12px',
        background: trueDataConnected ? '#d4edda' : '#f8d7da',
        border: `1px solid ${trueDataConnected ? '#c3e6cb' : '#f5c6cb'}`,
        borderRadius: '4px'
      }}>
        {trueDataConnected ? (
          <Wifi size={16} style={{ color: '#28a745' }} />
        ) : (
          <WifiOff size={16} style={{ color: '#dc3545' }} />
        )}
        <span style={{
          fontSize: '11px',
          fontWeight: '600',
          color: trueDataConnected ? '#155724' : '#721c24'
        }}>
          {trueDataMode === 'live' ? 'L' : trueDataMode === 'replay' ? 'R' : 'OFF'}
        </span>
      </div>

       {/* P/L Display */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ fontSize: '11px', fontWeight: '600' }}>P/L Rs</span>
        <div style={{
          padding: '3px 10px',
          border: timerRunning ? '2px solid #28a745' : '1px solid #d1d5db',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: timerRunning ? '900' : '600',
          color: realPnlRs >= 0 ? '#28a745' : '#dc3545',
          minWidth: '70px',
          textAlign: 'center',
          background: timerRunning ? '#c8e6c9' : 'white',
          boxShadow: timerRunning ? '0 0 50px rgba(40, 167, 69, 0.8)' : 'none',
          transition: 'all 0.1s ease'
        }}>
          {formatCurrency(realPnlRs)}
        </div>
        <span style={{ fontSize: '11px', fontWeight: '600' }}>%</span>
        <div style={{
          padding: '3px 10px',
          border: timerRunning ? '2px solid #28a745' : '1px solid #d1d5db',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: timerRunning ? '900' : '600',
          color: realPnlPercent >= 0 ? '#28a745' : '#dc3545',
          minWidth: '50px',
          textAlign: 'center',
          background: timerRunning ? '#c8e6c9' : 'white',
          boxShadow: timerRunning ? '0 0 50px rgba(40, 167, 69, 0.8)' : 'none',
          transition: 'all 0.1s ease'
        }}>
          {realPnlPercent >= 0 ? '+' : ''}{realPnlPercent.toFixed(2)}%
        </div>
      </div>
      {/* Time Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        <input
          type="text"
          value={formatTime(timerSeconds)}
          readOnly
          className={timerRunning ? 'timer-running' : ''}
          style={{
            padding: '3px 4px',
            border: timerRunning ? '2px solid #28a745' : '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '11px',
            width: '70px',
            background: timerRunning ? '#c8e6c9' : '#f8f9fa',
            fontWeight: timerRunning?'900':'500',
            textAlign: 'center',
            color: timerRunning ? '#1b5e20' : '#495057',
            boxShadow: timerRunning ? '0 0 50px rgba(40, 167, 69, 0.8)' : 'none',
            transition: 'all 0.1s ease'
          }}
        />
        <button
          onClick={handleResetTimer}
          style={{
            background: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            marginLeft: '4px'
          }}
          title="Reset Timer"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Trading Controls */}
      <div style={{
        background: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '4px',
        padding: '8px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <button
          onClick={handleTradeToggle}
          style={{
            background: tradeEnabled ? '#28a745' : '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <Power size={12} />
       T
        </button>

        <button
          onClick={handleCallToggle}
          disabled={!tradeEnabled}
          style={{
            background: callEnabled ? '#28a745' : '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: tradeEnabled ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            opacity: tradeEnabled ? 1 : 0.5
          }}
        >
          <Power size={12} />
          CE
        </button>

        <button
          onClick={handlePutToggle}
          disabled={!tradeEnabled}
          style={{
            background: putEnabled ? '#28a745' : '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: tradeEnabled ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            opacity: tradeEnabled ? 1 : 0.5
          }}
        >
          <Power size={12} />
          PE
        </button>
      </div>

       {/* Low Limit Check Controls */}
      <div style={{
        background: '#e7f3ff',
        border: '1px solid #b3d9ff',
        borderRadius: '4px',
        padding: '8px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <button
          onClick={() => {
            const newState = !lowLimitCheckEnabled;
            setLowLimitCheckEnabled(newState);
            updateSettings({ low_limit_check_xy: newState ? 'ON' : 'OFF' });
          }}
          style={{
            background: lowLimitCheckEnabled ? '#28a745' : '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '11px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          LL
        </button>
        <input
          type="number"
          value={lowLimitX}
          onChange={(e) => {
            const val = parseInt(e.target.value) || 0;
            setLowLimitX(val);
          }}
          onBlur={() => updateSettings({ low_limit_x: lowLimitX })}
          style={{
            width: '50px',
            padding: '3px 6px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '11px',
            textAlign: 'center'
          }}
          placeholder="X"
        />
        <input
          type="number"
          value={lowLimitY}
          onChange={(e) => {
            const val = parseInt(e.target.value) || 0;
            setLowLimitY(val);
          }}
          onBlur={() => updateSettings({ low_limit_y: lowLimitY })}
          style={{
            width: '50px',
            padding: '3px 6px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '11px',
            textAlign: 'center'
          }}
          placeholder="Y"
        />
      </div>

      {/* NIX15, CR, and IXL-N%L moved to LivePage.tsx (near the TGT row) per client's explicit
          ask — this toolbar only keeps the inputs/widgets that need to be visible everywhere. */}

      {/* Exit All Buttons - First Row */}
      <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto' }}>
        <button
          onClick={() => handleExitAllClick(exchange)}
          style={{
            background: exchange === 'NSE' ? '#dc3545' : '#8b1538',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 10px',
            fontSize: '11px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <AlertTriangle size={12} />
          Exit{openOrdersCount > 0 ? ` (${openOrdersCount})` : ''}
        </button>
      </div>



      {/* Exit All Confirmation Modal */}
      {showExitModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'hidden',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}>
            {/* Header */}
            <div style={{
              background: '#dc3545',
              color: 'white',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <AlertTriangle size={24} />
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Exit All {exitModalType} Positions</h3>
              </div>
              {!isExiting && (
                <button
                  onClick={() => setShowExitModal(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                >
                  <X size={20} />
                </button>
              )}
            </div>

            {/* Body */}
            <div style={{ padding: '20px', maxHeight: '400px', overflowY: 'auto' }}>
              {exitOrders.length === 0 ? (
                <div style={{
                  background: '#d1ecf1',
                  border: '1px solid #bee5eb',
                  borderRadius: '6px',
                  padding: '20px',
                  textAlign: 'center',
                  fontSize: '14px',
                  color: '#0c5460'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
                  <strong>No Open Positions</strong>
                  <div style={{ marginTop: '8px', fontSize: '13px' }}>There are no positions to exit at the moment.</div>
                </div>
              ) : (
                <>
                  <div style={{
                    background: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '6px',
                    padding: '12px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    color: '#856404'
                  }}>
                    <strong>Warning:</strong> This will exit all {exitOrders.length} open position(s). This action cannot be undone.
                  </div>

                  {/* Order Summary */}
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#495057' }}>
                      Open Positions ({exitOrders.length})
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {exitOrders.map((order) => (
                        <div
                          key={order.id}
                          style={{
                            background: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            padding: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#212529', marginBottom: '4px' }}>
                              {order.trading_symbol}
                            </div>
                            <div style={{ fontSize: '11px', color: '#6c757d' }}>
                              Qty: {order.quantity} | Type: {order.drv_option_type} | Entry: ₹{order.entry_ltp || order.strike_ltp}
                            </div>
                          </div>
                          <div style={{ marginLeft: '12px' }}>
                            {exitProgress[order.id] === 'pending' && (
                              <div style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                border: '2px solid #dee2e6',
                                background: 'white'
                              }} />
                            )}
                            {exitProgress[order.id] === 'processing' && (
                              <div style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                border: '3px solid #007bff',
                                borderTopColor: 'transparent',
                                animation: 'spin 0.8s linear infinite'
                              }} />
                            )}
                            {exitProgress[order.id] === 'success' && (
                              <CheckCircle size={24} style={{ color: '#28a745' }} />
                            )}
                            {exitProgress[order.id] === 'failed' && (
                              <X size={24} style={{ color: '#dc3545' }} />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  {isExiting && (
                    <div style={{ marginTop: '16px' }}>
                      <div style={{
                        background: '#e9ecef',
                        borderRadius: '4px',
                        height: '8px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          background: '#28a745',
                          height: '100%',
                          width: `${(Object.values(exitProgress).filter(s => s === 'success' || s === 'failed').length / exitOrders.length) * 100}%`,
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                      <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '6px', textAlign: 'center' }}>
                        {Object.values(exitProgress).filter(s => s === 'success' || s === 'failed').length} / {exitOrders.length} completed
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div style={{
              borderTop: '1px solid #dee2e6',
              padding: '16px 20px',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              {exitOrders.length === 0 ? (
                <button
                  onClick={() => setShowExitModal(false)}
                  style={{
                    background: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 20px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              ) : (
                !isExiting && (
                  <>
                    <button
                      onClick={() => setShowExitModal(false)}
                      style={{
                        background: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '8px 20px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmExitAll}
                      style={{
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '8px 20px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <AlertTriangle size={16} />
                      Confirm Exit All {exitModalType}
                    </button>
                  </>
                )
              )}
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Second Row */}
      <div style={{
        background: themeConfig.colors.surface,
        border: `1px solid ${themeConfig.colors.border}`,
        borderTop: 'none',
        borderRadius: '0 0 4px 4px',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        // Client's explicit ask: the widget groups had too much space between them —
        // 'space-between' was stretching them across the bar's full width on wide screens,
        // growing the gaps far past the `gap` value below. 'flex-start' packs them together,
        // with `gap` alone controlling the spacing (also reduced from 20px to 12px).
        justifyContent: 'flex-start',
        flexWrap: 'nowrap',
        gap: '12px',
        // Unlike the first row above, this one had no overflow handling at all — on a narrow
        // (tablet) viewport its many widgets just overflowed the bar's own width, forcing the
        // WHOLE PAGE into horizontal scroll instead of staying contained to this bar. Matches the
        // first row's own scroll-within-the-bar treatment instead of wrapping (wrapping would make
        // this bar very tall with this many distinct widget groups).
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}>
        {/* Delete Orders */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          {(['NSE', 'BSE'] as const).map(type => {
            const count = type === 'NSE' ? ordersCount : bseOrdersCount;
            const isDeleting = deleting && deleteType === type;
            return (
              <button
                key={type}
                onClick={() => { setDeleteType(type); setShowDeleteModal(true); }}
                disabled={deleting}
                style={{
                  background: isDeleting ? '#6c757d' : count > 0 ? '#8b1538' : '#f8d7da',
                  color: isDeleting ? 'white' : count > 0 ? 'white' : '#721c24',
                  border: count > 0 ? 'none' : '1px solid #f5c6cb',
                  borderRadius: '4px',
                  padding: '6px 12px',
                  fontSize: '11px',
                  fontWeight: '600',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  opacity: deleting ? 0.7 : 1
                }}
              >
                <Trash2 size={8} />
                {isDeleting ? 'Deleting...' : `${type} ${count > 0 ? ` (${count})` : ''}`}
              </button>
            );
          })}
        </div>

        {/* Separator */}
        <div style={{
          width: '1px',
          height: '30px',
          background: themeConfig.colors.border
        }}></div>

        {/* X% / X / X% Values */}
        {(() => {
          const tag = parseInt(tagValue);
          const xHighlight = tagValue !== '' && tag > 0 && parseInt(xCount) >= tag;
          return (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: xHighlight ? '#f0fdf4' : 'transparent',
              border: xHighlight ? '2px solid #155724' : '2px solid transparent',
              borderRadius: '4px',
              padding: '2px 6px',
              transition: 'all 0.2s ease'
            }}>
              <label style={{ fontSize: '9px', fontWeight: '600', color: xHighlight ? '#155724' : themeConfig.colors.text, whiteSpace: 'nowrap' }}>
                X%/X/%
              </label>
              <div style={{ display: 'flex', gap: '3px' }}>
                <input
                  type="number"
                  style={{
                    borderRadius: '3px',
                    padding: '4px 4px',
                    fontSize: '11px',
                    background: themeConfig.colors.surface,
                    width: '45px',
                    border: `1px solid ${themeConfig.colors.border}`,
                    color: themeConfig.colors.text,
                    textAlign: 'center'
                  }}
                  value={xPercentage}
                  readOnly
                  step="0.1"
                />
                <input
                  type="text"
                  style={{
                    borderRadius: '3px',
                    padding: '4px 4px',
                    fontSize: '11px',
                    background: xHighlight ? '#155724' : themeConfig.colors.primary + '20',
                    fontWeight: '600',
                    width: '35px',
                    border: xHighlight ? '1px solid #0d3b17' : `1px solid ${themeConfig.colors.border}`,
                    color: xHighlight ? '#ffffff' : themeConfig.colors.text,
                    textAlign: 'center'
                  }}
                  value={xCount}
                  readOnly
                />
                <input
                  type="text"
                  style={{
                    borderRadius: '3px',
                    padding: '4px 4px',
                    fontSize: '11px',
                    background: '#e3f2fd',
                    fontWeight: '600',
                    width: '45px',
                    border: '1px solid #90caf9',
                    color: '#1565c0',
                    textAlign: 'center'
                  }}
                  value={xPerAvg}
                  readOnly
                />
              </div>
            </div>
          );
        })()}

        {/* Separator */}
        <div style={{
          width: '1px',
          height: '30px',
          background: themeConfig.colors.border
        }}></div>

        {/* X/Y stock-pool mode: 1 = current active stocks, 2 = their futures symbols, 3 = fixed alternate 95-stock list */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title="Which stock pool feeds the X/Y calculation">
          {[1, 2, 3].map(mode => (
            <label
              key={mode}
              style={{
                display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer',
                fontSize: '10px', fontWeight: 600,
                color: xyStockMode === mode ? themeConfig.colors.primary : themeConfig.colors.text,
              }}
            >
              <input
                type="radio"
                name="xyStockMode"
                checked={xyStockMode === mode}
                onChange={() => updateXYStockMode(mode)}
                style={{ cursor: 'pointer', margin: 0 }}
              />
              {mode}
            </label>
          ))}
          {/* Client's explicit ask (temporary — for now, until they say otherwise): hide the eye
              icon (X/Y symbols modal trigger). Uncomment to bring it back. */}
          {/* <span
            onClick={openXYSymbolsModal}
            style={{ display: 'flex', cursor: 'pointer', marginLeft: '2px' }}
            title="Show symbols currently used for X/Y calculation"
          >
            <Eye size={13} style={{ color: themeConfig.colors.text }} />
          </span> */}
        </div>

        {/* Separator */}
        <div style={{
          width: '1px',
          height: '30px',
          background: themeConfig.colors.border
        }}></div>

        {/* Y% / Y / Y% Values */}
        {(() => {
          const tag = parseInt(tagValue);
          const yHighlight = tagValue !== '' && tag > 0 && parseInt(yCount) >= tag;
          return (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: yHighlight ? '#fdf0f0' : 'transparent',
              border: yHighlight ? '2px solid #8b1538' : '2px solid transparent',
              borderRadius: '4px',
              padding: '2px 6px',
              transition: 'all 0.2s ease'
            }}>
              <label style={{ fontSize: '9px', fontWeight: '600', color: yHighlight ? '#8b1538' : themeConfig.colors.text, whiteSpace: 'nowrap' }}>
                Y%/Y/%
              </label>
              <div style={{ display: 'flex', gap: '3px' }}>
                <input
                  type="number"
                  style={{
                    borderRadius: '3px',
                    padding: '4px 4px',
                    fontSize: '11px',
                    background: themeConfig.colors.surface,
                    width: '45px',
                    border: `1px solid ${themeConfig.colors.border}`,
                    color: themeConfig.colors.text,
                    textAlign: 'center'
                  }}
                  value={yPercentage}
                  readOnly
                  step="0.1"
                />
                <input
                  type="text"
                  style={{
                    borderRadius: '3px',
                    padding: '4px 4px',
                    fontSize: '11px',
                    background: yHighlight ? '#8b1538' : themeConfig.colors.secondary + '20',
                    fontWeight: '600',
                    width: '35px',
                    border: yHighlight ? '1px solid #6b0f2a' : `1px solid ${themeConfig.colors.border}`,
                    color: yHighlight ? '#ffffff' : themeConfig.colors.text,
                    textAlign: 'center'
                  }}
                  value={yCount}
                  readOnly
                />
                <input
                  type="text"
                  style={{
                    borderRadius: '3px',
                    padding: '4px 4px',
                    fontSize: '11px',
                    background: '#fce4ec',
                    fontWeight: '600',
                    width: '45px',
                    border: '1px solid #f48fb1',
                    color: '#c62828',
                    textAlign: 'center'
                  }}
                  value={yPerAvg}
                  readOnly
                />
              </div>
            </div>
          );
        })()}

        {/* Separator */}
        <div style={{ width: '1px', height: '30px', background: themeConfig.colors.border }}></div>

        {/* N / S Order Kill Switch */}
        <div style={{ display: 'flex', gap: '2px' }}>
          <button
            onClick={async () => {
              const next = !nseOrderEnabled;
              setNseOrderEnabled(next);
              const token = localStorage.getItem('token');
              await fetch(`${API_CONFIG.baseURL}/trade-settings`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ nse_order_enabled: next ? 'ON' : 'OFF', exchange })
              });
            }}
            style={{
              background: nseOrderEnabled ? '#28a745' : '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px 0 0 4px',
              padding: '4px 10px',
              fontSize: '11px',
              fontWeight: '700',
              cursor: 'pointer'
            }}
            title={nseOrderEnabled ? 'NSE orders ON — click to disable' : 'NSE orders OFF — click to enable'}
          >
            N
          </button>
          <button
            onClick={async () => {
              const next = !bseOrderEnabled;
              setBseOrderEnabled(next);
              const token = localStorage.getItem('token');
              await fetch(`${API_CONFIG.baseURL}/trade-settings`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ bse_order_enabled: next ? 'ON' : 'OFF', exchange })
              });
            }}
            style={{
              background: bseOrderEnabled ? '#28a745' : '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '0 4px 4px 0',
              padding: '4px 10px',
              fontSize: '11px',
              fontWeight: '700',
              cursor: 'pointer'
            }}
            title={bseOrderEnabled ? 'BSE orders ON — click to disable' : 'BSE orders OFF — click to enable'}
          >
            B
          </button>
        </div>

        {/* Separator */}
        <div style={{ width: '1px', height: '30px', background: themeConfig.colors.border }}></div>

        {/* NY Spread / SX Spread — Bullish/Bearish master switches (same source of truth as
            the NY SPREAD / SX SPREAD pages' own toggles — enabling/disabling from either place
            stays in sync). OFF here blocks new entries for that market+direction immediately;
            it does not touch any position that's already open. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '10px', fontWeight: '700', color: themeConfig.colors.textSecondary }}>NY</span>
          <div style={{ display: 'flex', gap: '2px' }}>
            <button
              onClick={() => toggleSpreadEnabled('NIFTY', 'BULLISH')}
              style={{
                background: isSpreadEnabled('NIFTY', 'BULLISH') ? '#28a745' : '#dc3545',
                color: 'white', border: 'none', borderRadius: '4px 0 0 4px',
                padding: '4px 8px', fontSize: '10px', fontWeight: '700', cursor: 'pointer',
              }}
              title={`NIFTY Spread BULLISH ${isSpreadEnabled('NIFTY', 'BULLISH') ? 'ON — click to disable' : 'OFF — click to enable'}`}
            >
              BU
            </button>
            <button
              onClick={() => toggleSpreadEnabled('NIFTY', 'BEARISH')}
              style={{
                background: isSpreadEnabled('NIFTY', 'BEARISH') ? '#28a745' : '#dc3545',
                color: 'white', border: 'none', borderRadius: '0 4px 4px 0',
                padding: '4px 8px', fontSize: '10px', fontWeight: '700', cursor: 'pointer',
              }}
              title={`NIFTY Spread BEARISH ${isSpreadEnabled('NIFTY', 'BEARISH') ? 'ON — click to disable' : 'OFF — click to enable'}`}
            >
              BR
            </button>
          </div>
          <span style={{ fontSize: '10px', fontWeight: '700', color: themeConfig.colors.textSecondary, marginLeft: '4px' }}>SX</span>
          <div style={{ display: 'flex', gap: '2px' }}>
            <button
              onClick={() => toggleSpreadEnabled('SENSEX', 'BULLISH')}
              style={{
                background: isSpreadEnabled('SENSEX', 'BULLISH') ? '#28a745' : '#dc3545',
                color: 'white', border: 'none', borderRadius: '4px 0 0 4px',
                padding: '4px 8px', fontSize: '10px', fontWeight: '700', cursor: 'pointer',
              }}
              title={`SENSEX Spread BULLISH ${isSpreadEnabled('SENSEX', 'BULLISH') ? 'ON — click to disable' : 'OFF — click to enable'}`}
            >
              BU
            </button>
            <button
              onClick={() => toggleSpreadEnabled('SENSEX', 'BEARISH')}
              style={{
                background: isSpreadEnabled('SENSEX', 'BEARISH') ? '#28a745' : '#dc3545',
                color: 'white', border: 'none', borderRadius: '0 4px 4px 0',
                padding: '4px 8px', fontSize: '10px', fontWeight: '700', cursor: 'pointer',
              }}
              title={`SENSEX Spread BEARISH ${isSpreadEnabled('SENSEX', 'BEARISH') ? 'ON — click to disable' : 'OFF — click to enable'}`}
            >
              BR
            </button>
          </div>
        </div>

        {/* Separator */}
        <div style={{ width: '1px', height: '30px', background: themeConfig.colors.border }}></div>

        {/* Buy LTP Diff */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: themeConfig.colors.text, minWidth: '70px' }}>
            {exchange === 'BSE' ? 'Buy' : 'Buy'}
          </label>
          <input
            type="text"
            style={{
              borderRadius: '3px',
              padding: '4px 6px',
              fontSize: '11px',
              background: buyLtpDiff.active ? 'white' : '#f8f9fa',
              fontWeight: '600',
              width: '80px',
              border: buyLtpDiff.active
                ? `2px solid ${buyLtpDiff.diff >= 0 ? '#28a745' : '#dc3545'}`
                : `1px solid ${themeConfig.colors.border}`,
              color: buyLtpDiff.active
                ? (buyLtpDiff.diff >= 0 ? '#28a745' : '#dc3545')
                : '#6c757d',
              textAlign: 'center'
            }}
            value={buyLtpDiff.active
              ? `${buyLtpDiff.diff >= 0 ? '+' : ''}${buyLtpDiff.diff.toFixed(2)}`
              : '--'
            }
            readOnly
            title={buyLtpDiff.active
              ? `Entry ${exchange === 'BSE' ? 'SENSEX' : 'NIFTY'}: ${buyLtpDiff.entryLtp}, Current: ${exchange === 'BSE' ? sensexLtp.toFixed(2) : niftyLtp.toFixed(2)}`
              : 'No active Buy position'}
          />
        </div>

        {/* Tag input moved to Live Page — tagValue itself stays here (still drives the X/Y
            count highlight above), it's just no longer editable from this bar. */}

        {/* Separator */}
        <div style={{
          width: '1px',
          height: '30px',
          background: themeConfig.colors.border
        }}></div>

        {/* Sell LTP Diff */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: themeConfig.colors.text, minWidth: '70px' }}>
            Sell
          </label>
          <input
            type="text"
            style={{
              borderRadius: '3px',
              padding: '4px 6px',
              fontSize: '11px',
              background: sellLtpDiff.active ? 'white' : '#f8f9fa',
              fontWeight: '600',
              width: '80px',
              border: sellLtpDiff.active
                ? `2px solid ${sellLtpDiff.diff >= 0 ? '#dc3545' : '#28a745'}`
                : `1px solid ${themeConfig.colors.border}`,
              color: sellLtpDiff.active
                ? (sellLtpDiff.diff >= 0 ? '#dc3545' : '#28a745')
                : '#6c757d',
              textAlign: 'center'
            }}
            value={sellLtpDiff.active
              ? `${sellLtpDiff.diff >= 0 ? '+' : ''}${sellLtpDiff.diff.toFixed(2)}`
              : '--'
            }
            readOnly
            title={sellLtpDiff.active
              ? `Entry ${exchange === 'BSE' ? 'SENSEX' : 'NIFTY'}: ${sellLtpDiff.entryLtp}, Current: ${exchange === 'BSE' ? sensexLtp.toFixed(2) : niftyLtp.toFixed(2)}`
              : 'No active Sell position'}
          />
        </div>

        {/* IXL-N%L moved to LivePage.tsx near the TGT row, same as NIX15 and CR above. */}

        {/* Separator */}
        {/* TSL — not needed (commented out per request) */}
        {/* <div style={{
          width: '1px',
          height: '30px',
          background: themeConfig.colors.border
        }}></div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: themeConfig.colors.text, minWidth: '25px' }}>
            TSL
          </label>
          <input
            type="text"
            style={{
              borderRadius: '3px',
              padding: '4px 6px',
              fontSize: '11px',
              background: themeConfig.colors.surface,
              width: '65px',
              border: `1px solid ${themeConfig.colors.border}`,
              color: themeConfig.colors.text,
              textAlign: 'center'
            }}
            value={tslValue}
            onChange={(e) => setTslValue(e.target.value)}
            onBlur={() => updateSettings({ tsl_value: tslValue })}
          />
        </div> */}


      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 0 10px rgba(40, 167, 69, 0.5);
            transform: scale(1);
          }
          50% {
            box-shadow: 0 0 20px rgba(40, 167, 69, 0.8);
            transform: scale(1.02);
          }
        }
        .timer-running {
          animation: pulse 2s ease-in-out infinite;
        }
      `}</style>

      {/* Delete Permanently Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', width: '90%',
            maxWidth: '500px', padding: '24px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            {(deleteType === 'NSE' ? ordersCount : bseOrdersCount) === 0 ? (
              // Empty state
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Orders Already Empty
                </h3>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '20px' }}>
                  There are no orders in the database to delete.
                </p>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  style={{
                    padding: '8px 24px', border: 'none', borderRadius: '6px',
                    fontSize: '13px', fontWeight: '600',
                    backgroundColor: '#6c757d', color: 'white', cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            ) : (
              // Confirm delete
              <>
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#8b1538', marginBottom: '8px' }}>
                    Delete {deleteType} Orders Permanently?
                  </h3>
                  <p style={{ fontSize: '14px', color: '#6c757d', margin: 0 }}>
                    This will permanently delete <strong>{deleteType === 'NSE' ? ordersCount : bseOrdersCount} {deleteType} orders</strong> from the database. This action cannot be undone!
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    style={{
                      padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px',
                      fontSize: '14px', fontWeight: '500', backgroundColor: 'white',
                      color: '#374151', cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePermanentDelete}
                    style={{
                      padding: '8px 16px', border: 'none', borderRadius: '6px',
                      fontSize: '14px', fontWeight: '500',
                      backgroundColor: '#8b1538', color: 'white', cursor: 'pointer'
                    }}
                  >
                    Delete Permanently
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* XY Stock Symbols Modal — shows exactly which symbols are live-feeding the X/Y calculation */}
      {showXYSymbolsModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', width: '90%',
            maxWidth: '520px', maxHeight: '70vh', padding: '24px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            display: 'flex', flexDirection: 'column'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', margin: 0 }}>
                  X/Y Calculation — Active Symbols
                </h3>
                {!xySymbolsLoading && !xySymbolsError && (
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0' }}>
                    Mode {xySymbolsData.mode} · {xySymbolsData.count} symbols
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowXYSymbolsModal(false)}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280' }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {xySymbolsLoading ? (
                <p style={{ fontSize: '13px', color: '#6b7280' }}>Loading...</p>
              ) : xySymbolsError ? (
                <p style={{ fontSize: '13px', color: '#dc2626' }}>{xySymbolsError}</p>
              ) : xySymbolsData.symbols.length === 0 ? (
                <p style={{ fontSize: '13px', color: '#6b7280' }}>No symbols loaded yet — check back once the market monitor has run at least one cycle.</p>
              ) : (
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: '6px 10px'
                }}>
                  {xySymbolsData.symbols.map(sym => (
                    <span key={sym} title={sym} style={{
                      fontSize: '12px', color: '#374151', backgroundColor: '#f3f4f6',
                      borderRadius: '4px', padding: '3px 6px', overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                      {sym}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Progress Modal */}
      {deleting && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '400px',
            padding: '24px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#212529', marginBottom: '8px' }}>
                Deleting Orders...
              </h3>
              <p style={{ fontSize: '14px', color: '#6c757d', margin: 0 }}>
                Please wait while we permanently delete all orders.
              </p>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                background: '#e9ecef',
                borderRadius: '8px',
                height: '8px',
                overflow: 'hidden'
              }}>
                <div style={{
                  background: '#8b1538',
                  height: '100%',
                  width: `${deleteProgress}%`,
                  transition: 'width 0.3s ease',
                  borderRadius: '8px'
                }} />
              </div>
              <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '8px' }}>
                {deleteProgress}% Complete
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '400px',
            padding: '24px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <CheckCircle size={48} style={{ color: '#28a745', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#212529', marginBottom: '8px' }}>
              Successfully Deleted!
            </h3>
            <p style={{ fontSize: '16px', color: '#6c757d', margin: 0 }}>
              {deletedCount} orders have been permanently deleted from database.
            </p>
            <button
              onClick={() => setShowSuccessModal(false)}
              style={{
                marginTop: '20px',
                padding: '8px 24px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: '#28a745',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingAppBar;